import { useEffect, useState } from 'react'

import { ActionStatus, type Post } from '@tribeplatform/gql-client/types'
import { useUpdateSubscription } from '@tribeplatform/react-sdk/hooks'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import {
  type FollowLevel,
  getPostFollowLevel,
  UnfollowedToast,
} from './followLevel.js'

/**
 * Optimistic follow level of a post. `applyLevel` subscribes at the given
 * level, or unsubscribes for null and offers an Undo toast.
 */
export const useFollowLevel = (post: Post) => {
  const { mutateAsync: updateSubscription } = useUpdateSubscription()
  const serverLevel = getPostFollowLevel(post)
  const [level, setLevel] = useState<FollowLevel>(serverLevel)

  useEffect(() => {
    setLevel(serverLevel)
  }, [serverLevel])

  const applyLevel = async (next: FollowLevel) => {
    const previous = level
    setLevel(next)
    try {
      const result = await updateSubscription(
        next
          ? {
              action: 'subscribe',
              publisherType: 'post',
              publisherId: post.id,
              level: next,
            }
          : {
              action: 'unsubscribe',
              publisherType: 'post',
              publisherId: post.id,
            },
      )
      if (result?.status === ActionStatus.failed) {
        setLevel(previous)
        return
      }
      if (next === null && previous !== null) {
        toast({
          custom: (
            <UnfollowedToast
              title={post.title || ''}
              onUndo={() => applyLevel(previous)}
            />
          ),
        })
      }
    } catch {
      setLevel(previous)
    }
  }

  return { level, following: level !== null, applyLevel }
}
